import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { lazy, Suspense, useState } from 'react';
import type { DialogProps } from '../dialogs/DialogProvider';
import type { LatLng, MapLandmark } from './MapPicker';

// The map library is large: loaded when the dialog opens.
const MapPicker = lazy(() => import('./MapPicker'));

/** "-33.416785" -> -33.416785; blank or not a number -> null. */
const toNumber = (v: string | null | undefined) => (v && v.trim() !== '' && Number.isFinite(Number(v)) ? Number(v) : null);
/** Text latitude and longitude as a point, or null unless both are numbers. */
export function pointOf(latitude: string | null | undefined, longitude: string | null | undefined): LatLng | null {
  const lat = toNumber(latitude);
  const lng = toNumber(longitude);
  return lat === null || lng === null ? null : { latitude: lat, longitude: lng };
}

/**
 * Pick a place by clicking on a map (satellite or plain), then drag the pin to adjust. Closes
 * with the point, to six decimal places (about 10 cm), or nothing when cancelled.
 */
export function MapPickerDialog({
  title,
  lead,
  value,
  centre,
  landmarks,
  close,
}: { title: string; lead?: string; value: LatLng | null; centre: LatLng | null; landmarks?: MapLandmark[] } & DialogProps<{ latitude: string; longitude: string }>) {
  const [point, setPoint] = useState<LatLng | null>(value);
  return (
    <Dialog open onClose={() => close()} maxWidth="md" fullWidth aria-labelledby="map-picker-title">
      <DialogTitle id="map-picker-title">{title}</DialogTitle>
      <DialogContent>
        <Typography color="textSecondary" sx={{ mb: 2 }}>
          {lead ?? 'Click the map to drop the pin, then drag it to adjust.'}
        </Typography>
        <Suspense fallback={<Skeleton variant="rounded" height={460} />}>
          <MapPicker value={value} centre={centre} landmarks={landmarks} onChange={setPoint} />
        </Suspense>
        <Typography variant="body2" color="textSecondary" className="num" sx={{ mt: 1.5, minHeight: 20 }}>
          {point ? `${point.latitude.toFixed(6)}, ${point.longitude.toFixed(6)}` : 'No pin yet'}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => close()}>Cancel</Button>
        <Button variant="contained" disabled={!point} onClick={() => point && close({ latitude: point.latitude.toFixed(6), longitude: point.longitude.toFixed(6) })}>
          Use this spot
        </Button>
      </DialogActions>
    </Dialog>
  );
}
